import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { palette } from "../../../../assets/styles/Palette";
import { H4, Body2, Sub1 } from "../../../../assets/styles/Typography";

const MoleculeMealEditPopup = ({
  content,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState("");
  const [editingMeal, setEditingMeal] = useState([]);
  const [showWeeklyView, setShowWeeklyView] = useState(false);

  // 초기 데이터 로드
  useEffect(() => {
    if (content) {
      setTitle(content.dataAnalysis?.title || "");
      setEditingMeal(content.contentGeneratorMeal?.meal_schedule || []);
      setShowWeeklyView(content.showWeeklyView || false);
    }
  }, [content]);

  const getStartIndex = () => {
    if (!editingMeal || editingMeal.length === 0) return -1;
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const index = editingMeal.findIndex((item) => item.date >= todayStr);
    return index;
  };

  const [startIndex, setStartIndex] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // editingMeal이 로드되면 인덱스 설정
  useEffect(() => {
    const idx = getStartIndex();
    setStartIndex(idx);
    setSelectedIndex(idx !== -1 ? idx : -1);
  }, [editingMeal]);

  // 날짜 포맷 (2025-11-17 -> 11/17)
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}/${day}`;
  };

  // 메뉴 변경 핸들러
  const handleMenuChange = (type, text) => {
    const newMealData = [...editingMeal];
    const menuArray = text.split("\n");
    
    if (newMealData[selectedIndex]) {
        // 객체 깊은 복사 (참조 끊기)
        newMealData[selectedIndex] = { ...newMealData[selectedIndex] };

        if (type === "lunch") {
            newMealData[selectedIndex].lunch_menu = menuArray;
        } else {
            newMealData[selectedIndex].dinner_menu = menuArray;
        }
        setEditingMeal(newMealData);
    }
  };

  // 원산지 변경 핸들러
  const handleOriginChange = (type, text) => {
    const newMealData = [...editingMeal];
    if (newMealData[selectedIndex]) {
      newMealData[selectedIndex] = { ...newMealData[selectedIndex] };
      if (type === "lunch") {
        newMealData[selectedIndex].lunch_origins = text;
      } else {
        newMealData[selectedIndex].dinner_origins = text;
      }
      setEditingMeal(newMealData);
    }
  };

  // 저장 핸들러
  const handleSaveClick = () => {
      onSave({ title, meal: editingMeal, showWeeklyView: showWeeklyView }); 
  };

  const currentData = (editingMeal && editingMeal.length > selectedIndex && selectedIndex !== -1) ? editingMeal[selectedIndex] : null;

  // 변경 여부 확인
  const isTitleChanged = title !== (content?.dataAnalysis?.title || "");
  const isMealChanged =
    JSON.stringify(editingMeal) !==
    JSON.stringify(content?.contentGeneratorMeal?.meal_schedule || []);
  const isWeeklyViewChanged = showWeeklyView !== (content?.showWeeklyView || false);
  
  const isModified = isTitleChanged || isMealChanged || isWeeklyViewChanged;

  return (
    <MealEditPopupOverlay>
      <MealEditPopupContainer>
        <MealEditPopupHeader>
          <H4 color="gray800">식단표 수정</H4>
          <CloseButton onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#666666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </MealEditPopupHeader>
        <MealEditPopupBody>
          {/* 제목 수정 필드 */}
          <TitleEditSection>
            <TitleLabel>
              <Body2 color="gray700">콘텐츠 제목</Body2>
            </TitleLabel>
            <TitleInput
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="콘텐츠 제목을 입력하세요"
            />
          </TitleEditSection>

          {/* 주간 식단표 활성화 여부 */}
          <WeeklyViewSection>
            <TitleLabel>
              <Body2 color="gray700">주간 식단표 활성화 여부</Body2>
            </TitleLabel>
            <RadioGroup>
              <RadioLabel>
                <input
                  type="radio"
                  name="weeklyView"
                  checked={showWeeklyView === false}
                  onChange={() => setShowWeeklyView(false)}
                />
                <Body2 color="gray800" style={{ fontWeight: 400 }}>
                  비활성화
                </Body2>
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  name="weeklyView"
                  checked={showWeeklyView === true}
                  onChange={() => setShowWeeklyView(true)}
                />
                <Body2 color="gray800" style={{ fontWeight: 400 }}>
                  활성화
                </Body2>
              </RadioLabel>
            </RadioGroup>
          </WeeklyViewSection>

          {/* 식단 탭 (날짜 선택) */}
          <DateTabsContainer>
            {[0, 1, 2, 3, 4].map((offset) => {
              let idx = -1;
              let data = null;

              if (startIndex !== -1) {
                idx = startIndex + offset;
                data = editingMeal[idx];
              }

              // 표시할 날짜 텍스트 결정
              let dateText = "-";
              if (data) {
                dateText = formatDate(data.date);
              } else if (startIndex === -1 && offset === 0) {
                // 데이터가 없어도 첫 번째 탭은 오늘 날짜 표시
                dateText = formatDate(new Date());
              }

              const isActive =
                (startIndex !== -1 && idx === selectedIndex) ||
                (startIndex === -1 && offset === 0);

              return (
                <DateTab
                  key={offset}
                  active={isActive}
                  disabled={!data}
                  onClick={() => data && setSelectedIndex(idx)}
                >
                  <DateText active={isActive}>
                    {dateText}
                  </DateText>
                </DateTab>
              );
            })}
          </DateTabsContainer>

          {/* 식단 수정 영역 */}
          <MealContentContainer>
            {currentData ? (
              <>
                <MealBox>
                    <MealLabel>중식</MealLabel>
                    <MealTextarea 
                        value={currentData.lunch_menu?.join("\n") || ""}
                        onChange={(e) => handleMenuChange("lunch", e.target.value)}
                        placeholder="중식 메뉴를 입력하세요 (줄바꿈으로 구분)"
                    />
                    <OriginSection>
                        <OriginLabel>원산지</OriginLabel>
                        <OriginInput
                            value={currentData.lunch_origins || ""}
                            onChange={(e) => handleOriginChange("lunch", e.target.value)}
                            placeholder="원산지를 입력하세요"
                        />
                    </OriginSection>
                </MealBox>
                <MealBox>
                    <MealLabel>석식</MealLabel>
                    <MealTextarea 
                        value={currentData.dinner_menu?.join("\n") || ""}
                        onChange={(e) => handleMenuChange("dinner", e.target.value)}
                        placeholder="석식 메뉴를 입력하세요 (줄바꿈으로 구분)"
                    />
                    <OriginSection>
                        <OriginLabel>원산지</OriginLabel>
                        <OriginInput
                            value={currentData.dinner_origins || ""}
                            onChange={(e) => handleOriginChange("dinner", e.target.value)}
                            placeholder="원산지를 입력하세요"
                        />
                    </OriginSection>
                </MealBox>
              </>
            ) : (
                <NoDataContainer>
                    <Sub1 color="gray500" style={{ textAlign: "center", whiteSpace: "pre-wrap" }}>
                        {`표시할 식단 데이터가 없습니다.\n마지막 식단 데이터는 ${
                          editingMeal.length > 0
                            ? formatDate(editingMeal[editingMeal.length - 1].date)
                            : "-"
                        }입니다.`}
                    </Sub1>
                </NoDataContainer>
            )}
          </MealContentContainer>

        </MealEditPopupBody>
        <MealEditPopupFooter>
          <ButtonRow>
            <ButtonCancel onClick={onClose}>
              <ButtonTextCancel>취소</ButtonTextCancel>
            </ButtonCancel>
            <ButtonPrimary
              onClick={handleSaveClick}
              disabled={!isModified}
            >
              <ButtonText>저장</ButtonText>
            </ButtonPrimary>
          </ButtonRow>
        </MealEditPopupFooter>
      </MealEditPopupContainer>
    </MealEditPopupOverlay>
  );
};

export default MoleculeMealEditPopup;

const MealEditPopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
`;

const MealEditPopupContainer = styled.div`
  background: ${palette.white};
  border-radius: 15px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 4px 4px 30px rgba(0, 0, 0, 0.15);
`;

const MealEditPopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid ${palette.outlineGray};
`;

const MealEditPopupBody = styled.div`
  padding: 32px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`;

const TitleEditSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleLabel = styled.div`
  display: flex;
  align-items: center;
`;

const TitleInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${palette.outlineGray};
  border-radius: 8px;
  font-size: 16px;
  font-family: Pretendard;
  color: ${palette.gray800};
  background: ${palette.white};

  &:focus {
    outline: none;
    border-color: ${palette.primary};
  }

  &::placeholder {
    color: ${palette.gray500};
  }
`;

const MealEditPopupFooter = styled.div`
  padding: 24px 32px;
  border-top: 1px solid ${palette.outlineGray};
  display: flex;
  justify-content: flex-end;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const ButtonCancel = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: ${palette.white};
  border: 1px solid ${palette.primary};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: ${palette.gray50};
  }
`;

const ButtonPrimary = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: ${palette.primary};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: ${palette.primaryDark};
  }

  &:disabled {
    background: #9e9e9e;
    cursor: not-allowed;
  }
`;

const ButtonText = styled.span`
  color: ${palette.white};
  font-size: 17px;
  font-weight: 400;
  font-family: Pretendard;
`;

const ButtonTextCancel = styled.span`
  color: ${palette.primary};
  font-size: 17px;
  font-weight: 400;
  font-family: Pretendard;
`;

// 식단 수정 전용 스타일
const WeeklyViewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
  padding-left: 4px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  input[type="radio"] {
    appearance: none;
    width: 20px;
    height: 20px;
    border: 2px solid ${palette.gray300};
    border-radius: 50%;
    margin: 0;
    cursor: pointer;
    position: relative;

    &:checked {
      border-color: ${palette.primary};
      background-color: ${palette.white};

      &::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: ${palette.primary};
      }
    }
  }
`;

const DateTabsContainer = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-start;
`;

const DateTab = styled.button`
    padding: 8px 24px;
    border-radius: 8px;
    border: 2px solid ${(props) => (props.active ? palette.primary : palette.outlineGray)};
    background: ${palette.white};
    cursor: pointer;
    transition: all 0.2s;
    min-width: 80px;
    
    &:hover {
        border-color: ${(props) => (props.disabled ? palette.outlineGray : palette.primary)};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
        background: ${palette.gray50};
    }
`;

const DateText = styled.span`
    font-size: 16px;
    font-weight: ${(props) => (props.active ? "700" : "400")};
    color: ${(props) => (props.active ? palette.primary : palette.gray600)};
`;

const MealContentContainer = styled.div`
    display: flex;
    gap: 20px;
    height: 400px;
`;

const MealBox = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: ${palette.white};
    border-radius: 12px;
    border: 2px solid ${palette.gray800}; /* 요청하신 이미지의 굵은 테두리 스타일 반영 */
    padding: 20px;
    overflow: hidden;
`;

const MealLabel = styled.div`
    font-size: 18px;
    font-weight: 700;
    color: ${palette.gray800};
    text-align: center;
    margin-bottom: 10px;
`;

const MealTextarea = styled.textarea`
    width: 100%;
    flex: 1;
    border: none;
    resize: none;
    font-size: 16px;
    line-height: 1.6;
    font-family: Pretendard;
    color: ${palette.gray800};
    outline: none;

    &::placeholder {
        color: ${palette.gray400};
    }
`;

const OriginSection = styled.div`
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-top: 1px solid ${palette.gray200};
    padding-top: 16px;
`;

const OriginLabel = styled.span`
    font-size: 14px;
    color: ${palette.gray600};
    font-weight: 500;
`;

const OriginInput = styled.input`
    width: 100%;
    padding: 12px;
    border: 1px solid ${palette.outlineGray};
    border-radius: 4px;
    font-size: 14px;
    font-family: Pretendard;
    color: ${palette.gray800};
    
    &:focus {
        outline: none;
        border-color: ${palette.primary};
    }

    &::placeholder {
        color: ${palette.gray400};
    }
`;

const NoDataContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${palette.gray50};
    border-radius: 12px;
`;
